import { Form, Formik } from 'formik';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { COMMAND_STATE, ENROLL_TYPE, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../../constants/command-status';
import { EnrolSubCmds } from '../../../constants/sub-commands';
import { useLocale } from '../../../contexts/localeContext';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../../contexts/websocket/IcaWebsocketContext';
import { EnrolmentDevice } from "../../../models/EnrolmentDevices";
import { EnrolmentTask } from '../../../models/EnrolmentTask';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { Finger } from '../../../models/Finger';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Name } from '../../../models/Name';
import { Tag } from '../../../models/Tag';
import WizardSideBarStep from '../../../models/WizardSideBarStep';
import { getBiometricNamesWithPagination } from '../../../services/biometricService';
import { saveCredential } from '../../../services/credentialsService';
import { enrolFinger, executeCommandForSavingTemplate } from '../../../services/executeCommandService';
import { getErrorMessage } from '../../../services/utilities/ServiceError';
import enrollCmd from '../../../services/wsPluginService/plugins/base/enrollCmd';
import { IShowEnrollValidOptions } from '../../../services/wsPluginService/plugins/base/pluginModels';
import { getEnrollResultCommandType } from '../../../services/wsPluginService/plugins/decodePluginMsg';
import { buildCardData, generateNewRandomPAC } from '../../../utilities/CredentialUtil';
import { appendFilter } from '../../../utilities/filters';
import FullScreenActivityFrame from '../../activity-frame/full-screen-activity-spinner';
import { useDialog } from '../../dialog/DialogContext';
import Wizard from '../../wizard/wizard';
import ChooseCard from '../choose-card';
import DetailsForm from '../details-form';
import SelectDeviceForm from '../select-device-form';
import SubTasksForm from '../sub-tasks-form';
import SelectFinger from './select-finger';
import SummaryForm from './summary-from';

interface Props {
    availablePlugins: IShowEnrollValidOptions;
    availableSystemDeviceIds: number[],
    availabilityResolved: boolean;
    cardInfo: Tag;
    onClose: () => void;
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onBack: (event: any) => void;
    updateBadge: (badge: number) => void;
    websocket: WebSocket | undefined;
    hasTags: boolean;
    masterId: number | undefined;
    selectedBiometricBrand?: string;
}

const EnrolFingerWizard: FC<Props> = ({ availablePlugins, cardInfo, onClose, defaultContent, defaultSteps, onBack, updateBadge, availableSystemDeviceIds, availabilityResolved, websocket, hasTags, masterId = 0, selectedBiometricBrand }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const [device, setDevice] = useState<Name>();
    const [listParameters, setListParameters] = useState<ListQuery>();
    const [deviceType, setDeviceType] = useState('');
    const [busyScanning, setBusyScanning] = useState<boolean>(false);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const enrolmentTask = new EnrolmentTask(translate);
    const [isHikFinger, setHikFinger] = useState<any>(false);
    const [pluginResponse, setPluginResponse] = useState<string>();
    const [selectedTask, setSelectedTask] = useState<string>();
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();
    const [newCard, setNewCard] = useState<Tag>(cardInfo);
    const [isNewCard, setIsNewCard] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<MultiSelectOption>({ value: "", label: "" });
    const [selectedFinger, setSelectedFinger] = useState<Finger>({ index: 0, label: '', value: '' })

    const onMessage = (response: any) => {
        const jsonData = JSON.parse(response.data);
        if (jsonData.command === MONITORING_COMMAND.MONITORING_ENROL && jsonData.attributes.subCommand === ENROLL_TYPE.ENROLL_IDEMIA_FINGER) {
            if (jsonData.attributes.cmdState === COMMAND_STATE.COMMAND_COMPLETE) {
                toast.success(translate.byKey(TranslationKeys.credentialSaved));
                updateBadge(1);
            }
            else {
                toast.warn(jsonData.attributes.errMessage);
            }
            onClose();
            setBusyScanning(false);
        }
    }

    const onMessagePluginInstance = (data: any) => {
        if (getEnrollResultCommandType(data) === enrollCmd.CMD_MORPHO || getEnrollResultCommandType(data) === enrollCmd.CMD_CONTROLID) {
            setPluginResponse(data);
        }
    }

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    useEffect(() => {
        if (pluginResponse) {
            commitTemplateResponse(pluginResponse);
        }
    }, [pluginResponse])

    const onLocalNext = (event, isFirstStep = false) => {
        if (isFirstStep) {
            if (event.task) {
                setSelectedTask(event.task);
    
                if (event.task === enrolmentDevice.CONTROLID_IDBIO.value) {
                    setDeviceType(event.description);
                    const allFingerDevices = [...enrolmentDevice.ENROL_FINGER_PLUGIN, ...enrolmentDevice.ENROL_FINGER_DEVICES,];
                    const selectedDeviceId = allFingerDevices.find((item) => item.label === event.description)?.id.toString();
                    if (selectedDeviceId) {
                        setListParameters({
                            ...listParameters,
                            filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []),
                        });
                    }

                } else if (event.task === enrolmentDevice.MORPHO.value) {
                    setDeviceType(event.description);
                    const allFingerDevices = [...enrolmentDevice.ENROL_FINGER_PLUGIN, ...enrolmentDevice.ENROL_FINGER_DEVICES,];
                    const selectedDeviceId = allFingerDevices.find((item) => item.label === event.description)?.id.toString();
                    if (selectedDeviceId) {
                        setListParameters({
                            ...listParameters,
                            filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []),
                        });
                    }

                } else if (event.task === enrolmentDevice.HIK_VISION_FINGER.value) {
                    setHikFinger(true);
                    //no plugin support for hik
                    const allFingerDevices = [...enrolmentDevice.ENROL_FINGER_DEVICES,];
                    const selectedDeviceId = allFingerDevices.find((item) => item.label === event.description)?.id.toString();
                    if (selectedDeviceId) {
                        setListParameters({
                            ...listParameters,
                            filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []),
                        });
                    }
                }
            }
        } else {
            if (event.code) {
                setSelectedTag({ ...selectedTag, label: event.code });
            }
        }
    
        setStep((prevStep) => prevStep + 1);
    };

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onBack(event);
            return;
        } else if (step === 2) {
            setDeviceType("")
        };

        setStep(step - 1);
    }

    const onEnrolFinger = async (values) => {
        // Selected Idemia (MSO300)
        if (selectedTask && selectedTask === enrolmentDevice.MORPHO.value) {
            if (websocket && websocket?.readyState === WebSocket.OPEN) {
                websocket?.send(`{'enrollType':'morpho','consolidate':'true','finger':'${selectedFinger.value}','enrollForDeviceType':'SINGLE'}`);
                websocket.onmessage = (value) => {
                    onMessagePluginInstance(value.data)
                };
                setBusyScanning(true);
            }
        }
        // Selected Control iD (iDBio)
        else if (selectedTask && selectedTask === enrolmentDevice.CONTROLID_IDBIO.value) {
            if (websocket && websocket?.readyState === WebSocket.OPEN) {
                websocket?.send(`{'enrollType':'controlid','consolidate':'true','finger':'${selectedFinger.value}','enrollForDeviceType':'SINGLE'}`);
                websocket.onmessage = (value) => {
                    onMessagePluginInstance(value.data)
                };
                setBusyScanning(true);
            }
        }
        // Selected hikVision
        else if (selectedTask && selectedTask === enrolmentDevice.HIK_VISION_FINGER.value) {
            await onHikVisionEnrol({ values });
        }
        else {
            console.log(`Unsupported finger enrolment type selected`);
        }
    }

    const onHikVisionEnrol = async ({ values }) => {
        try { setBusyScanning(true);    
        let newTag: any;
            if (isNewCard) {
                newTag = await saveCredential({...buildCardData({ ...values, masterId }, translate)});
            }

            const command: ExecuteCommand = {
                cmd: ENROLL_TYPE.ENROLL,
                cmdSubType: ENROLL_TYPE.ENROLL_HIKVISION_FINGER,
                tagId: newTag ? newTag.content.data.id : selectedTag.value,
                captureType: selectedFinger.index,
                deviceId: device?.value
            };

            const response = await enrolFinger(command);

            if (response.success === true && response.data['cmd-sub-type'] === ENROLL_TYPE.ENROLL_HIKVISION_FINGER) {
                if (response.data['cmd-status'] === COMMAND_STATE.COMMAND_FAILED) {
                   toast.warn(translate.byKey(response.data['cmd']));
                } else {
                    toast.success(translate.byKey(TranslationKeys.credentialSaved));
                }
                updateBadge(1);
            }

        } catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error)
            });
        } finally {
            setBusyScanning(false);
        }
    };

    const commitTemplateResponse = async (pluginManagerResponse: any) => {
        const jsonResponse = JSON.parse(pluginManagerResponse);

        if (!jsonResponse.enrollData) {
            setPluginResponse(undefined)
            setBusyScanning(false)
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.savingError) })
            return;
        }
        try {
            //Create new tag if needed
            let newTag: any;
            if (isNewCard) {
                newTag = await saveCredential({ ...buildCardData(newCard, translate), masterId: masterId });
            }
            //Build template information and save 
            const command: ExecuteCommand = {
                cmd: ENROLL_TYPE.ENROLL,
                cmdSubType: (selectedTask === enrolmentDevice.MORPHO.value) ? EnrolSubCmds.Enroll_MSO300_Response : EnrolSubCmds.Enroll_IdBio_Response,
                tagId: newTag ? newTag.content.data.id : selectedTag.value,
                finger: selectedFinger.index,
                jsonResponse: pluginManagerResponse,
            }
            const response: any = await executeCommandForSavingTemplate(command);
            if (response.success) {
                toast.success(translate.byKey(TranslationKeys.credentialSaved));
                updateBadge(1);
            }

        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        } finally {
            onClose();
            setPluginResponse(undefined)
            setBusyScanning(false)
        }
    }

    const getExtraStep = (): WizardSideBarStep[] => {
        if (isHikFinger) { //simply becasue you now need to select the device
            return [
                {
                    title: translate.byKey(TranslationKeys.selectDevice),
                    description: device?.label ?? '',
                    substeps: []
                },
            ]
        }
        return [];
    }

    const onChooseCardNext = (e) => {
        if (e.value && e.value === enrolmentDevice.MANUAL_CODE_TYPE.value) {
            setNewCard({
                ...e.values,
                code: cardInfo.code,
                typeId: cardInfo.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: ''
            });
            setIsNewCard(true);
        } else if (e.value && e.value === enrolmentDevice.RANDOM_PAC.value) {
            const tag: Tag = generateNewRandomPAC(newCard, translate)
            setNewCard({
                ...e.values,
                type: tag.type,
                code: tag.code,
                tagDisplay: tag.code,
                typeId: tag.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: ''
            });
            setSelectedTag({ ...selectedTag, label: tag.code })
            setIsNewCard(true);
        } else {
            setSelectedTag({ value: e.value, label: e.code })
            setStep(step + 1);
        }
    }

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: deviceType,
            substeps: []
        },
        ...getExtraStep(),
        {
            title: translate.byKey(TranslationKeys.finger),
            description: selectedFinger.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.card),
            description: selectedTag.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const getExtraContent = () => {
        if (isHikFinger) {
            return [

                <SelectDeviceForm
                    dataMethod={getBiometricNamesWithPagination}
                    onNext={(event) => onLocalNext(event)}
                    onBack={(event) => onLocalBack(event)}
                    setSelectedDevice={setDevice}
                    listParameters={listParameters} />
            ]
        }
        return [];
    }

    const content = [
        ...defaultContent,
        <SubTasksForm
            onNext={(event) => onLocalNext(event, true)}
            pluginsAvailable={availablePlugins}
            onBack={(event) => onLocalBack(event)}
            enrollMethod={enrolmentTask.ENROL_FINGERPRINT.value}
            availabilityResolved={availabilityResolved}
            selectedBiometricBrand={selectedBiometricBrand}
            availableSystemDeviceIds={availableSystemDeviceIds} />,
        ...getExtraContent(),
        <SelectFinger
            onNext={(event) => onLocalNext(event, false)}
            onBack={(event) => onLocalBack(event)}
            setSelectedFinger={setSelectedFinger} />,

        isNewCard ?
            <DetailsForm
                onBack={(e) => { setIsNewCard(false); }}
                onNext={onLocalNext}
                cardInfo={newCard} /> :
            <ChooseCard
                masterId={masterId}
                onBack={onLocalBack}
                onNext={onChooseCardNext} />,

        <SummaryForm
            onBack={(event) => onLocalBack(event)}
            taskDescription={enrolmentTask.ENROL_FINGERPRINT.label}
            device={deviceType}
            cardCode={selectedTag.label}
            selectedFinger={selectedFinger.label} />
    ];

    return <>
        {busyScanning ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.scanning)} /> : null}
        <Formik
            enableReinitialize={true}
            initialValues={newCard}
            onSubmit={onEnrolFinger}>
            <Form>
                <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                    step={step}
                    onClose={() => onClose()}
                    sidebarSteps={steps}>
                    {content}
                </Wizard>
            </Form>
        </Formik>
    </>
}
export default EnrolFingerWizard;